import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useCallback, useMemo, useRef, useLayoutEffect } from "react";
import { createPortal, flushSync } from "react-dom";
import MainLayout from "../../layouts/Layout";
import { CloseOutlined, CommentOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useSecureService } from "../../store/service";
import { useAuth } from "../../hooks/useAuth";
import { Button, Row, Col } from "antd";
import { useAntdAppMessage } from "../../hooks/useAntdAppMessage";
import { useLanguage } from "../../contexts/LanguageContext";
import { Shape, DrawingTool } from "../../components/VideoPlayer/VideoPlayer";
import styles from "./Projects.module.scss";
import { ApiFilePostStatusEnum } from "../../service/apis/FileApi";
import { fileNameWithoutExtension } from "../../utils/stringHelpers";
import FooterActions from './FooterActions';
import FileVersions from './FileVersions';
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import breadcrumbStyles from "../../components/Breadcrumb/Breadcrumb.module.scss";

import ApprovalModal from '../../modals/approvalModal';
import StatusSelector from '../../components/StatusSelector/StatusSelector';


// New components
import CommentSection, { CommentType } from '../../components/CommentSection/CommentSection';
import VideoPlayerSection from '../../components/VideoPlayerSection/VideoPlayerSection';
import CompactDrawingToolbox from '../../components/DrawingToolbox/CompactDrawingToolbox';
import VersionManager from '../../components/VersionManager/VersionManager';
import { useFileOperations } from '../../hooks/useFileOperations';
import { flattenCommentsWithNestedReplies } from '../../utils/flattenVersionComments';
import { canChangeEntryStatus, Role } from '../../constants/roles';
import {
  getVisibleVersionsForViewer,
  findVersionByHash,
  pickLatestVersion,
} from '../../utils/fileDetailVersionPolicy';
import { isApiAccessDenied } from '../../utils/apiAccess';
import ProjectFolderEmptyState from '../../components/ProjectFolderEmptyState/ProjectFolderEmptyState';
import { getProjectsBasePathFromLocation } from '../../utils/projectScope';
import { setFileDetailThemeOverride } from '../../utils/fileDetailThemeOverride';
import {
  buildSiblingFilesFromParentChildren,
  filterSiblingFiles,
  getParentEntryUrlFromBreadcrumbItems,
  getSiblingFileNavigationIndexByPath,
  isFileEntry,
  normalizeSiblingNavPath,
  sortSiblingFilesForNavigation,
  type BreadcrumbApiItem,
} from '../../utils/siblingFileNavigation';
import { formatApiUtcToLocaleDate, getApiUtcTimeMs } from '../../utils/apiDateTime';
import { markInPlaceSiblingNav } from '../../utils/inPlaceSiblingNav';

const FILE_DETAIL_MOBILE_HEADER_SLOT_ID = 'file-detail-mobile-header-slot';

/** File detail her zaman internal (yeşil) tema kullanır. */
const FILE_DETAIL_VARIANT = 'internal' as const;

const FILE_DETAIL_THEME_VARS: Record<string, string> = {
  '--app-accent-color': '#00C271',
  '--app-accent-color-soft': '#00C27180',
  '--app-accent-hover': '#00B366',
  '--app-accent-active': '#00A35C',
  '--app-accent-rgb': '0, 194, 113',
  '--app-accent-secondary': '#4CAF50',
  '--app-accent-secondary-rgb': '76, 175, 80',
  '--app-divider-color': '#00462C',
  '--app-glow-color': 'rgba(15, 111, 70, 0.5)',
};

type ViewerMediaKind = 'video' | 'image' | 'pdf' | 'other';

function viewerMediaKindFromFile(fileName: string, fileUrl: string): ViewerMediaKind {
  let extension: string | undefined;
  if (fileName && typeof fileName === 'string' && fileName.includes('.')) {
    const parts = fileName.split('.');
    if (parts.length > 1) extension = parts.pop()?.toLowerCase();
  }
  if ((!extension || extension === fileName?.toLowerCase()) && fileUrl) {
    const cleanUrl = fileUrl.split('?')[0].split('#')[0];
    const urlExtension = cleanUrl.split('.').pop()?.toLowerCase().trim();
    if (urlExtension && urlExtension.length <= 5) {
      const cleanExtension = urlExtension.replace(/[^a-z0-9]/gi, '');
      if (cleanExtension) extension = cleanExtension;
    }
  }
  if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension || '')) return 'video';
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'ico'].includes(extension || '')) return 'image';
  if (extension === 'pdf') return 'pdf';
  return 'other';
}

interface FileDetailProps {
  fileDetail?: any;
  onBack?: () => void;
  breadcrumbPathParts: any[];
  bclocation: any;
}

const FileDetail: React.FC<FileDetailProps> = ({ fileDetail: propFileDetail, onBack ,breadcrumbPathParts, bclocation}) => {
  const message = useAntdAppMessage();
  const { t } = useLanguage();
  const { projectName } = useParams<{ projectName: string }>();
  const location = useLocation();
  const isVersionsPageRoute = useMemo(
    () => location.pathname.endsWith('/versions'),
    [location.pathname]
  );
  const navigate = useNavigate();
  const {
    user,
    isGuest,
    isAnonymous,
    canComment,
    canResolve,
    canShowAllComments,
    canCreate,
  } = useAuth();
  const { getFileById, getEntryByPath, getBreadcrumb, changeStatus, createFile } = useSecureService();
  
  // Main file state
  const [fileDetail, setFileDetail] = useState<any>(propFileDetail || null);
  const [siblingFiles, setSiblingFiles] = useState<any[]>([]);
  const [siblingFilesLoading, setSiblingFilesLoading] = useState(false);
  const [fileSwitchLoading, setFileSwitchLoading] = useState(false);
  const [breadcrumbApiItems, setBreadcrumbApiItems] = useState<BreadcrumbApiItem[] | null>(null);
  const [loadAccessDenied, setLoadAccessDenied] = useState(false);
  const [loading, setLoading] = useState(!propFileDetail);
  const [isDrawingMode] = useState(true);

  // Version states
  const [versions, setVersions] = useState<any[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<number>(1);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState<string>('');
  const [videoLoading, setVideoLoading] = useState(false);

  // Compare player states
  const [selectedVersionCompare, setSelectedVersionCompare] = useState<number | null>(null);
  const [currentVideoUrlCompare, setCurrentVideoUrlCompare] = useState<string>('');
  const [, setVideoLoadingCompare] = useState(false);

  // Drawing states - Main player
  const [shapes, setShapes] = useState<Shape[]>([]);
  const [drawingTool, setDrawingTool] = useState<DrawingTool>('none');
  const drawingToolRef = useRef<DrawingTool>(drawingTool);
  drawingToolRef.current = drawingTool;
  const [drawingColor, setDrawingColor] = useState('#00C271');
  const [selectedShapeId, setSelectedShapeId] = useState<string | null>(null);

  // Drawing states - Compare player
  const [shapesCompare, setShapesCompare] = useState<Shape[]>([]);
  const [selectedShapeIdCompare, setSelectedShapeIdCompare] = useState<string | null>(null);

  // Undo/Redo states - Main player
  const [drawingHistory, setDrawingHistory] = useState<Array<{
    shapes: Shape[];
    selectedShapeId: string | null;
  }>>([{ shapes: [], selectedShapeId: null }]);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(0);

  // Undo/Redo states - Compare player
  const [drawingHistoryCompare, setDrawingHistoryCompare] = useState<Array<{
    shapes: Shape[];
    selectedShapeId: string | null;
  }>>([{ shapes: [], selectedShapeId: null }]);
  const [currentHistoryIndexCompare, setCurrentHistoryIndexCompare] = useState(0);

  // Video time states
  const [currentVideoTime, setCurrentVideoTime] = useState(0);
  const [currentVideoTimeCompare, setCurrentVideoTimeCompare] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);

  // Range states
  const [selectedRange, setSelectedRange] = useState<[number, number] | null>(null);
  const [externalRange, setExternalRange] = useState<[number, number] | null>(null);
  const [selectedRangeCompare, setSelectedRangeCompare] = useState<[number, number] | null>(null);
  const [externalRangeCompare, setExternalRangeCompare] = useState<[number, number] | null>(null);

  // Frame states
  const [currentFrameIndex, setCurrentFrameIndex] = useState<number | null>(null);
  const [currentFrameIndexCompare, setCurrentFrameIndexCompare] = useState<number | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isPausedCompare, setIsPausedCompare] = useState(false);
  const [selectedFrame, setSelectedFrame] = useState<number | null>(null);
  const [selectedFrameRange, setSelectedFrameRange] = useState<[number, number] | null>(null);
  const [selectedFrameCompare, setSelectedFrameCompare] = useState<number | null>(null);
  const [selectedFrameRangeCompare, setSelectedFrameRangeCompare] = useState<[number, number] | null>(null);
  const [currentFrameRange, setCurrentFrameRange] = useState<string>('');
  const [currentFrameRangeCompare, setCurrentFrameRangeCompare] = useState<string>('');

  const [pdfCurrentPage, setPdfCurrentPage] = useState(1);
  const pdfPageRef = useRef(1);
  const [pdfNumPages, setPdfNumPages] = useState<number | null>(null);
  const pdfJumpSeqRef = useRef(0);
  const [pdfPageJumpRequest, setPdfPageJumpRequest] = useState<{
    token: number;
    page: number;
  } | null>(null);
  /** PDF comment list: true = all pages; false after user changes page (checkbox can turn all pages back on) */
  const [pdfShowAllPageComments, setPdfShowAllPageComments] = useState(true);

  // Compare mode states
  const [compareMode, setCompareMode] = useState(false);
  const [activePlayer, setActivePlayer] = useState(1);
  
  // Comment states
  const [showAllComments, setShowAllComments] = useState(false);
  const [selectedCommentId, setSelectedCommentId] = useState<number | null>(null);
  /** Yorum seçimi hangi player’da yapıldı; seçim kalkınca o canvas’taki çizimi temizlemek için */
  const commentSelectionSourcePlayerRef = useRef<1 | 2 | null>(null);
  const selectedCommentIdRef = useRef<number | null>(null);
  /** Reply yazarken: parent çizimi gizli, boş tuval; null = normal yorum görüntüleme */
  const [replyCompositionPlayer, setReplyCompositionPlayer] = useState<1 | 2 | null>(null);

  // Drawing UI states
  const [closeDrawingPopover, setCloseDrawingPopover] = useState(false);
  // Status change states
// Status change states - sadece approved için modal
// const [statusLoading, setStatusLoading] = useState(false);
// const [selectedStatusForModal, setSelectedStatusForModal] = useState<string>('NeedsReview');
  const [isApprovalModalVisible, setIsApprovalModalVisible] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);
  // Modal states
  const addVersionInputRef = useRef<HTMLInputElement>(null);
  const addVersionTargetPanelRef = useRef<1 | 2 | null>(null);
  const skipVersionEffectRef = useRef(false);
  const skipPlaybackResetRef = useRef(false);
  const inPlaceSiblingTargetIdRef = useRef<number | null>(null);
  const fileSwitchPendingRef = useRef(false);
  // Footer actions states - seçili version için
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [selectedItemsDetails, setSelectedItemsDetails] = useState<Array<{ id: number; name: string; entryType: string; status?: string; share?: any[]; thumbnail?: any }>>([]);

  useEffect(() => {
    selectedCommentIdRef.current = selectedCommentId;
  }, [selectedCommentId]);

  const FPS = 30;

  useLayoutEffect(() => {
    setFileDetailThemeOverride(true);
    return () => setFileDetailThemeOverride(false);
  }, []);

  // Parent (sibling barındıran klasör/proje) yalnızca breadcrumb'dan gelir.
  // URL düz yapıda (int/<uuid>) olduğu için path'ten türetilemez.
  const siblingParentKey = useMemo(
    () => getParentEntryUrlFromBreadcrumbItems(breadcrumbApiItems),
    [breadcrumbApiItems]
  );

  const resetDrawingToolbarAfterCommentSend = useCallback(() => {
    setDrawingTool('none');
    setSelectedShapeId(null);
    setSelectedShapeIdCompare(null);
    const empty = { shapes: [] as Shape[], selectedShapeId: null as string | null };
    setDrawingHistory([empty]);
    setCurrentHistoryIndex(0);
    setDrawingHistoryCompare([empty]);
    setCurrentHistoryIndexCompare(0);
  }, []);

  const hideDrawingToolbarForViewer = user?.role === Role.Viewer;

  const showDrawingToolbox = useMemo(() => {
    if (!fileDetail || hideDrawingToolbarForViewer) return false;
    const getFileType = (fileName: string, videoUrl?: string) => {
      let extension: string | undefined = undefined;
      if (fileName && fileName.includes('.')) {
        const parts = fileName.split('.');
        if (parts.length > 1) {
          extension = parts.pop()?.toLowerCase();
        }
      }
      if ((!extension || extension === fileName.toLowerCase()) && videoUrl) {
        const cleanUrl = videoUrl.split('?')[0].split('#')[0];
        const urlExtension = cleanUrl.split('.').pop()?.toLowerCase().trim();
        if (urlExtension && urlExtension.length <= 5) {
          const cleanExtension = urlExtension.replace(/[^a-z0-9]/gi, '');
          if (cleanExtension) {
            extension = cleanExtension;
          }
        }
      }
      if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension || '')) {
        return 'video';
      }
      if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'ico'].includes(extension || '')) {
        return 'image';
      }
      if (extension === 'pdf') {
        return 'pdf';
      }
      return 'other';
    };
    const fileUrl =
      currentVideoUrl ||
      fileDetail?.versions?.find((v: any) => v.id === selectedVersion)?.fileUrl ||
      fileDetail?.versions?.[0]?.fileUrl ||
      fileDetail?.url ||
      fileDetail?.fileUrl ||
      fileDetail?.downloadUrl ||
      fileDetail?.path ||
      '';
    const selectedVersionData = fileDetail.versions?.find((v: any) => v.id === selectedVersion);
    const fileName =
      selectedVersionData?.name ||
      (selectedVersionData?.path ? String(selectedVersionData.path).split('/').pop() : undefined) ||
      fileDetail?.name ||
      '';
    const ft = getFileType(fileName, fileUrl);
    return ft === 'video' || ft === 'image' || ft === 'pdf';
  }, [fileDetail, selectedVersion, currentVideoUrl, hideDrawingToolbarForViewer]);

  const primaryViewerMediaKind = useMemo((): ViewerMediaKind => {
    if (!fileDetail) return 'other';
    const fileUrl =
      currentVideoUrl ||
      fileDetail?.versions?.find((v: any) => v.id === selectedVersion)?.fileUrl ||
      fileDetail?.versions?.[0]?.fileUrl ||
      fileDetail?.url ||
      fileDetail?.fileUrl ||
      fileDetail?.downloadUrl ||
      fileDetail?.path ||
      '';
    const selectedVersionData = fileDetail.versions?.find((v: any) => v.id === selectedVersion);
    const fileName =
      selectedVersionData?.name ||
      (selectedVersionData?.path ? String(selectedVersionData.path).split('/').pop() : undefined) ||
      fileDetail?.name ||
      '';
    return viewerMediaKindFromFile(fileName, fileUrl);
  }, [fileDetail, selectedVersion, currentVideoUrl]);

  const fileDetailCompareUiActive = compareMode && primaryViewerMediaKind === 'video';

  const { handleDelete, handleDownload, handleCopyUrl } = useFileOperations({
    fileDetail,
    selectedVersion: fileDetailCompareUiActive ? (selectedVersionCompare ?? selectedVersion) : selectedVersion,
    versions,
    location,
  });

  const requestPdfPageJump = useCallback((page: number) => {
    pdfJumpSeqRef.current += 1;
    setPdfPageJumpRequest({ token: pdfJumpSeqRef.current, page });
  }, []);

  const handlePdfPageChange = useCallback((page: number) => {
    pdfPageRef.current = page;
    setPdfCurrentPage(page);
  }, []);

  const handlePdfUserNavigate = useCallback(() => {
    setPdfShowAllPageComments(false);
  }, []);

  useEffect(() => {
    pdfPageRef.current = 1;
    setPdfCurrentPage(1);
    setPdfNumPages(null);
    setPdfPageJumpRequest(null);
    pdfJumpSeqRef.current = 0;
    setPdfShowAllPageComments(true);
  }, [currentVideoUrl, selectedVersion]);

  useEffect(() => {
    if (primaryViewerMediaKind !== 'pdf') {
      setPdfShowAllPageComments(true);
    }
  }, [primaryViewerMediaKind]);

  useEffect(() => {
    if (drawingTool === 'none') return;
    setIsPaused(true);
    setIsPausedCompare(true);
  }, [drawingTool]);

  const closeCompareMode = useCallback(() => {
    setCompareMode(false);
    setShowAllComments(false);
    setReplyCompositionPlayer(null);
    setSelectedCommentId(null);
    setActivePlayer(1);
    setShapesCompare([]);
    setSelectedShapeIdCompare(null);
    setCurrentFrameIndexCompare(null);
    setSelectedFrameCompare(null);
    setSelectedFrameRangeCompare(null);
    setSelectedRangeCompare(null);
    setExternalRangeCompare(null);
    setCurrentVideoTimeCompare(0);
    setIsPausedCompare(false);
  }, []);

  /** Compare modundan çıkarken aktif paneldeki versiyonu ana seçime taşı */
  const exitCompareFromToolbar = useCallback(() => {
    if (activePlayer === 2 && selectedVersionCompare != null) {
      setSelectedVersion(selectedVersionCompare);
      const v = versions.find((x: any) => x.id === selectedVersionCompare);
      if (v?.name) {
        window.location.hash = v.name;
      }
    }
    closeCompareMode();
  }, [activePlayer, selectedVersionCompare, versions, closeCompareMode]);

  /** Compare panel başlığı X: sol panel kapanınca sağdaki versiyon ana olur; sağ panel kapanınca sol kalır */
  const handleComparePanelClose = useCallback(
    (panel: 1 | 2) => {
      if (panel === 1 && selectedVersionCompare != null) {
        setSelectedVersion(selectedVersionCompare);
        const v = versions.find((x: any) => x.id === selectedVersionCompare);
        if (v?.name) {
          window.location.hash = v.name;
        }
      }
      closeCompareMode();
    },
    [selectedVersionCompare, versions, closeCompareMode],
  );

  useEffect(() => {
    if (primaryViewerMediaKind !== 'video' && compareMode) {
      closeCompareMode();
    }
  }, [primaryViewerMediaKind, compareMode, closeCompareMode]);

  // Fetch file detail effect - propFileDetail varsa (ProjectDetail'den geliyorsa) tekrar getEntryByPath çağırma
  useEffect(() => {
    const fetchFileDetail = async () => {
      setLoadAccessDenied(false);
      let fullPath = location.pathname.substring(1);
      if (fullPath.endsWith('/versions')) {
        fullPath = fullPath.replace('/versions', '');
      }

      // URL tek doğruluk kaynağı: propFileDetail URL ile uyuşmuyorsa stale'dir.
      // In-place sibling nav sırasında ProjectDetail eski entry'yi (StrictMode/async yarış)
      // geç set edip aşağı itebiliyor; bu eski prop fileDetail'i geri çevirmemeli.
      const urlNavKey = normalizeSiblingNavPath(fullPath);
      const propNavKey = normalizeSiblingNavPath(propFileDetail?.path || '');
      if (propFileDetail && propNavKey && urlNavKey && propNavKey !== urlNavKey) {
        setLoading(false);
        return;
      }

      // In-place sibling nav: handleNavigateToSibling zaten getFileById ile fileDetail'ı set etti.
      // Yeni propFileDetail aynı dosyaysa tekrar istek atma. (Bellekteki ref; F5'te null = tam yükleme.)
      if (
        inPlaceSiblingTargetIdRef.current != null &&
        propFileDetail?.id === inPlaceSiblingTargetIdRef.current
      ) {
        inPlaceSiblingTargetIdRef.current = null;
        setLoading(false);
        return;
      }

      if (propFileDetail) {
        if (propFileDetail.id) {
          try {
            const fileResponse = await getFileById(propFileDetail.id);
            if (isApiAccessDenied(fileResponse)) {
              setLoadAccessDenied(true);
              setFileDetail(null);
            } else if (fileResponse.success && fileResponse.data) {
              setFileDetail({ ...fileResponse.data, entryType: propFileDetail.entryType, path: propFileDetail.path });
            } else {
              setFileDetail(propFileDetail);
            }
          } catch {
            setFileDetail(propFileDetail);
          }
        } else {
          setFileDetail(propFileDetail);
        }
        setLoading(false);
        resetAllStates();
        return;
      }

      if (fullPath) {
        setLoading(true);
        try {
          const entryResponse = await getEntryByPath(fullPath);
          if (isApiAccessDenied(entryResponse)) {
            setLoadAccessDenied(true);
            setFileDetail(null);
          } else if (entryResponse.success && entryResponse.data) {
            const entryData = entryResponse.data;
            if (entryData.id) {
              const fileResponse = await getFileById(entryData.id);
              if (isApiAccessDenied(fileResponse)) {
                setLoadAccessDenied(true);
                setFileDetail(null);
              } else if (fileResponse.success && fileResponse.data) {
                setFileDetail({ ...fileResponse.data, entryType: entryData.entryType, path: entryData.path });
              } else {
                setFileDetail(entryData);
              }
            } else {
              setFileDetail(entryData);
            }
            resetAllStates();
          } else {
            setFileDetail(null);
          }
        } catch (error) {
          setFileDetail(null);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchFileDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only pathname/role/prop trigger refetch
  }, [propFileDetail, projectName, location.pathname, user?.role]);

  // 1) Breadcrumb'ı çek (parent'ı buradan öğreniyoruz). getBreadcrumb cache'li.
  // fileDetail.id'ye bağlı: yenilemede auth/data hazır olunca güvenle tetiklenir,
  // sibling navigasyonunda da yeni dosya yüklenince yeniden çalışır.
  useEffect(() => {
    let cancelled = false;
    const urlPath = normalizeSiblingNavPath(location.pathname);
    // Tercihen fileDetail.path (kanonik), yoksa URL.
    const breadcrumbPath = normalizeSiblingNavPath(fileDetail?.path || '') || urlPath;

    if (!breadcrumbPath || !fileDetail?.id) {
      return;
    }

    (async () => {
      try {
        const breadcrumbResponse = await getBreadcrumb(breadcrumbPath);
        if (cancelled) return;
        const items = breadcrumbResponse?.success
          ? (breadcrumbResponse as { data?: { items?: BreadcrumbApiItem[] } }).data?.items
          : undefined;
        if (Array.isArray(items) && items.length > 0) {
          setBreadcrumbApiItems(items);
        }
      } catch {
        /* sibling navigasyonu opsiyonel; sessizce geç */
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable service fn
  }, [location.pathname, fileDetail?.id, fileDetail?.path]);

  // 2) Parent değiştiğinde (yalnızca farklı klasöre geçince) sibling listesini çek.
  useEffect(() => {
    let cancelled = false;

    if (!siblingParentKey) {
      setSiblingFiles([]);
      return;
    }

    setSiblingFilesLoading(true);
    (async () => {
      try {
        const response = await getEntryByPath(siblingParentKey);
        if (cancelled) return;
        if (response?.success && response.data?.children) {
          setSiblingFiles(
            sortSiblingFilesForNavigation(
              buildSiblingFilesFromParentChildren(response.data.children)
            )
          );
        } else {
          setSiblingFiles([]);
        }
      } catch {
        if (!cancelled) setSiblingFiles([]);
      } finally {
        if (!cancelled) setSiblingFilesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when parent folder changes
  }, [siblingParentKey]);

  const finishFileSwitchLoading = useCallback(() => {
    fileSwitchPendingRef.current = false;
    setFileSwitchLoading(false);
  }, []);

  useEffect(() => {
    if (!fileSwitchPendingRef.current) return;
    if (versionsLoading || videoLoading) return;

    const timer = window.setTimeout(() => {
      finishFileSwitchLoading();
    }, 80);

    return () => window.clearTimeout(timer);
  }, [fileDetail?.id, versionsLoading, videoLoading, finishFileSwitchLoading]);

  const handleNavigateToSibling = useCallback(
    async (sibling: any) => {
      if (!isFileEntry(sibling)) return;
      if (!sibling?.id) return;
      if (!sibling?.path || typeof sibling.path !== 'string') return;
      // URL tek doğruluk kaynağı: hedef zaten mevcut URL ise atla (fileDetail.id stale olabilir).
      if (
        normalizeSiblingNavPath(sibling.path) ===
        normalizeSiblingNavPath(window.location.pathname)
      ) {
        return;
      }
      if (fileSwitchPendingRef.current) return;

      const cleanPath = sibling.path.trim().startsWith('/')
        ? sibling.path.trim()
        : `/${sibling.path.trim()}`;

      inPlaceSiblingTargetIdRef.current = sibling.id;
      fileSwitchPendingRef.current = true;
      setFileSwitchLoading(true);
      if (compareMode) closeCompareMode();

      try {
        const fileResponse = await getFileById(sibling.id);
        if (isApiAccessDenied(fileResponse)) {
          setLoadAccessDenied(true);
          setFileDetail(null);
          finishFileSwitchLoading();
          return;
        }
        if (!fileResponse?.success || !fileResponse.data) {
          message.error(t('fileDetail.fileNotFound'));
          finishFileSwitchLoading();
          return;
        }

        const siblingUrl = normalizeSiblingNavPath(sibling.path);
        setBreadcrumbApiItems((prev) => {
          if (!Array.isArray(prev) || prev.length === 0) return prev;
          const updated = [...prev];
          updated[updated.length - 1] = {
            name: fileResponse.data.name ?? sibling.name ?? '',
            url: siblingUrl,
          };
          return updated;
        });

        resetAllStates();
        setFileDetail({
          ...fileResponse.data,
          entryType: 'File',
          path: sibling.path || fileResponse.data.path,
        });

        // In-place nav sinyali: location.state DEĞİL (F5'te stale kalıyor), bellekteki bayrak.
        markInPlaceSiblingNav();
        navigate(cleanPath, {
          replace: true,
          state: {
            fromProjectsBasePath: getProjectsBasePathFromLocation(location),
          },
        });
      } catch {
        message.error(t('fileDetail.fileNotFound'));
        finishFileSwitchLoading();
      }
    },
    [
      fileDetail?.id,
      compareMode,
      closeCompareMode,
      finishFileSwitchLoading,
      getFileById,
      navigate,
      location,
      message,
      t,
    ]
  );

  // Mobil: player overlay okları yerine meta satırında "◀ 4/5 ▶" göstereceğiz.
  const navigableSiblingFiles = useMemo(
    () => sortSiblingFilesForNavigation(filterSiblingFiles(siblingFiles)),
    [siblingFiles]
  );
  const currentSiblingIndex = useMemo(
    () =>
      getSiblingFileNavigationIndexByPath(
        navigableSiblingFiles,
        normalizeSiblingNavPath(location.pathname)
      ),
    [navigableSiblingFiles, location.pathname]
  );
  const canGoPrevSibling = currentSiblingIndex > 0;
  const canGoNextSibling =
    currentSiblingIndex >= 0 && currentSiblingIndex < navigableSiblingFiles.length - 1;
  const siblingNavBusy = siblingFilesLoading || fileSwitchLoading;

  const navigateSiblingByOffset = useCallback(
    (direction: 'prev' | 'next') => {
      if (navigableSiblingFiles.length <= 1 || currentSiblingIndex < 0) return;
      const target =
        navigableSiblingFiles[currentSiblingIndex + (direction === 'prev' ? -1 : 1)];
      if (target) handleNavigateToSibling(target);
    },
    [navigableSiblingFiles, currentSiblingIndex, handleNavigateToSibling]
  );

  // Reset all states helper
  const resetAllStates = () => {
        setCurrentVideoTime(0);
        setCurrentFrameIndex(null);
        setSelectedFrame(null);
        setSelectedFrameRange(null);
        setCurrentFrameRange('');
    setSelectedRange(null);
    setExternalRange(null);
        setShapes([]);
        setIsPaused(false);
        setSelectedFrameCompare(null);
        setSelectedFrameRangeCompare(null);
    setSelectedRangeCompare(null);
    setExternalRangeCompare(null);
        setShapesCompare([]);
        setCurrentVideoTimeCompare(0);
        setCurrentFrameIndexCompare(null);
        setIsPausedCompare(false);
        // Comment seçim state'lerini sıfırla
        setReplyCompositionPlayer(null);
        setSelectedCommentId(null);
        setActivePlayer(1);
    };

  const applyVersionVisibility = useCallback(
    (opts?: { honorSkip: boolean }) => {
      if (!fileDetail?.versions?.length) {
        setVersions([]);
        setSelectedVersion(1);
        setVersionsLoading(false);
        return;
      }
      const fileVersions = fileDetail.versions;
      const honorSkip = opts?.honorSkip !== false;
      if (honorSkip && skipVersionEffectRef.current) {
        skipVersionEffectRef.current = false;
        setVersions(fileVersions);
        setVersionsLoading(false);
        return;
      }
      setVersionsLoading(true);
      try {
        const hash = window.location.hash.replace('#', '');
        const { visibleVersions, selectedVersionId } = getVisibleVersionsForViewer(
          fileVersions,
          { user, isGuest, isAnonymous },
          hash
        );
        setVersions(visibleVersions);
        setSelectedVersion(selectedVersionId);
        if (isGuest && user) {
          const h = window.location.hash.replace('#', '');
          if (h && visibleVersions.length > 0) {
            const allowedOk = findVersionByHash(visibleVersions, h);
            if (!allowedOk) {
              const best = pickLatestVersion(visibleVersions) || visibleVersions[0];
              if (best?.name) {
                const newH = `#${best.name}`;
                if (window.location.hash !== newH) {
                  window.history.replaceState(
                    null,
                    '',
                    `${window.location.pathname}${window.location.search}${newH}`
                  );
                }
              }
            }
          }
        }
      } catch {
        setVersions([]);
        setSelectedVersion(1);
      } finally {
        setVersionsLoading(false);
      }
    },
    [fileDetail, user, isGuest, isAnonymous]
  );

  useEffect(() => {
    applyVersionVisibility({ honorSkip: true });
  }, [applyVersionVisibility]);

  useEffect(() => {
    if (!fileDetail?.versions?.length) return;
    const onHash = () => {
      if (skipVersionEffectRef.current) return;
      applyVersionVisibility({ honorSkip: false });
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [fileDetail, applyVersionVisibility]);

  /** Yorum / sayaç / pin: policy ile menüde görünen versiyonlar */
  const versionsForComments = useMemo(() => {
    if (!fileDetail?.versions) return [];
    const allowed = new Set(versions.map((v: any) => v.id));
    return fileDetail.versions.filter((v: any) => allowed.has(v.id));
  }, [fileDetail?.versions, versions]);

  // Update video URL when version changes
  useEffect(() => {
    const preservePlayback = skipPlaybackResetRef.current;
    if (preservePlayback) {
      skipPlaybackResetRef.current = false;
    }

    let newUrl = '';
    if (selectedVersion && versions.length > 0) {
      const selectedVersionData = versions.find((v: any) => v.id === selectedVersion);
      if (selectedVersionData) {
        newUrl = selectedVersionData.url || selectedVersionData.fileUrl || selectedVersionData.downloadUrl || selectedVersionData.path || '';
        
        // Footer actions için selectedItems güncelle
        setSelectedItems(new Set([selectedVersion]));
        setSelectedItemsDetails([{
          id: selectedVersion,
          name: selectedVersionData.name || fileDetail?.name || 'Version',
          entryType: 'Version', // Version seçiliyken entryType Version olmalı
          status: selectedVersionData.status || 'NeedsReview', // Version'ın status'u
          share: fileDetail?.share,
          thumbnail: selectedVersionData.thumbnailUrl || fileDetail?.thumbnailUrl
        }]);
      }
    } else if (fileDetail) {
      newUrl = fileDetail.url || fileDetail.fileUrl || fileDetail.downloadUrl || fileDetail.path || '';
      
      // Footer actions için fileDetail'ı selectedItems olarak ayarla
      setSelectedItems(new Set([fileDetail.id]));
      setSelectedItemsDetails([{
        id: fileDetail.id,
        name: fileDetail.name || 'File',
        entryType: fileDetail.entryType || 'File',
        status: fileDetail.status,
        share: fileDetail.share,
        thumbnail: fileDetail.thumbnailUrl
      }]);
    }

    if (newUrl && newUrl !== currentVideoUrl) {
      setVideoLoading(true);
      setCurrentVideoUrl(newUrl);
      setTimeout(() => {
        setVideoLoading(false);
      }, 500);
    }

    if (preservePlayback) {
      return;
    }

    // Reset drawing states when version changes
    setShapes([]);
    setSelectedShapeId(null);
    setCurrentFrameIndex(null);
    setSelectedRange(null);
    setExternalRange(null);
    setExternalRangeCompare(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when version/detail changes
  }, [selectedVersion, versions, fileDetail]);

  // Update compare video URL
  useEffect(() => {
    if (!fileDetailCompareUiActive || !selectedVersionCompare) {
      setCurrentVideoUrlCompare('');
      setVideoLoadingCompare(false);
      return;
    }

    let newUrl = '';
    if (selectedVersionCompare && versions.length > 0) {
      const selectedVersionData = versions.find((v: any) => v.id === selectedVersionCompare);
      if (selectedVersionData) {
        newUrl = selectedVersionData.url || selectedVersionData.fileUrl || selectedVersionData.downloadUrl || selectedVersionData.path || '';
        
        // Compare mode'da footer actions için selectedItems güncelle
        if (fileDetailCompareUiActive) {
          setSelectedItems(new Set([selectedVersionCompare]));
          setSelectedItemsDetails([{
            id: selectedVersionCompare,
            name: selectedVersionData.name || fileDetail?.name || 'Version',
            entryType: 'Version', // Version seçiliyken entryType Version olmalı
            status: selectedVersionData.status || 'NeedsReview', // Version'ın status'u
            share: fileDetail?.share,
            thumbnail: selectedVersionData.thumbnailUrl || fileDetail?.thumbnailUrl
          }]);
        }
      }
    }

    if (newUrl && newUrl !== currentVideoUrlCompare) {
      setVideoLoadingCompare(true);
      setCurrentVideoUrlCompare(newUrl);
      setTimeout(() => {
        setVideoLoadingCompare(false);
      }, 500);
    }

    // Reset compare player states
    setShapesCompare([]);
    setSelectedShapeIdCompare(null);
    setCurrentFrameIndexCompare(null);
    setSelectedRangeCompare(null);
    setExternalRangeCompare(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync URL when compare selection changes
  }, [selectedVersionCompare, versions, fileDetailCompareUiActive]);

  // Reset compare mode states
  useEffect(() => {
    if (!compareMode) {
      setSelectedVersionCompare(null);
      setCurrentVideoUrlCompare('');
      setVideoLoadingCompare(false);
      setShapesCompare([]);
      setSelectedShapeIdCompare(null);
      setCurrentFrameIndexCompare(null);
      setSelectedRangeCompare(null);
      setExternalRangeCompare(null);
      
      // Normal mode'a dönerken selectedItems'ı main version'a göre güncelle
      if (selectedVersion && versions.length > 0) {
        const selectedVersionData = versions.find((v: any) => v.id === selectedVersion);
        if (selectedVersionData) {
          setSelectedItems(new Set([selectedVersion]));
          setSelectedItemsDetails([{
            id: selectedVersion,
            name: selectedVersionData.name || fileDetail?.name || 'Version',
            entryType: 'Version', // Version seçiliyken entryType Version olmalı
            status: selectedVersionData.status || 'NeedsReview', // Version'ın status'u
            share: fileDetail?.share,
            thumbnail: selectedVersionData.thumbnailUrl || fileDetail?.thumbnailUrl
          }]);
        }
      } else if (fileDetail) {
        setSelectedItems(new Set([fileDetail.id]));
        setSelectedItemsDetails([{
          id: fileDetail.id,
          name: fileDetail.name || 'File',
          entryType: fileDetail.entryType || 'File',
          status: fileDetail.status,
          share: fileDetail.share,
          thumbnail: fileDetail.thumbnailUrl
        }]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only compareMode toggle
  }, [compareMode]);

  // Global compare version listener (compare is only supported for video in file detail)
  useEffect(() => {
    const checkForCompareVersion = () => {
      const globalSelectedVersion = (window as any).selectedVersionForCompare;
      if (!globalSelectedVersion) return;
      if (primaryViewerMediaKind !== 'video') {
        (window as any).selectedVersionForCompare = null;
        return;
      }
      setCompareMode(true);
      setSelectedVersionCompare(globalSelectedVersion);
      (window as any).selectedVersionForCompare = null;
    };

    const interval = setInterval(checkForCompareVersion, 100);
    return () => clearInterval(interval);
  }, [primaryViewerMediaKind]);

  // Frame selection handlers
  const handleFrameSelect = (frame: number) => {
    setSelectedFrame(frame);
    setSelectedFrameRange(null);
  };

  const handleFrameRangeSelect = (start: number, end: number) => {
    setSelectedFrame(null);
    setSelectedFrameRange([start, end]);
  };

  const handleFrameSelectCompare = (frame: number) => {
    setSelectedFrameCompare(frame);
    setSelectedFrameRangeCompare(null);
  };

  const handleFrameRangeSelectCompare = (start: number, end: number) => {
    setSelectedFrameCompare(null);
    setSelectedFrameRangeCompare([start, end]);
  };

  // Range selection handlers
  const handleRangeSelect = (range: [number, number]) => {
    const startFrame = Math.round(range[0] * FPS);
    const endFrame = Math.round(range[1] * FPS);
    
    if (startFrame === endFrame) {
      handleFrameSelect(startFrame);
      setCurrentFrameRange(`${startFrame}`);
    } else {
      handleFrameRangeSelect(startFrame, endFrame);
      setCurrentFrameRange(`${startFrame}-${endFrame}`);
    }
    
    setSelectedRange(range);
    setExternalRange(null);
  };

  const handleRangeSelectCompare = (range: [number, number]) => {
    const startFrame = Math.round(range[0] * FPS);
    const endFrame = Math.round(range[1] * FPS);
    
    if (startFrame === endFrame) {
      handleFrameSelectCompare(startFrame);
  } else {
      handleFrameRangeSelectCompare(startFrame, endFrame);
    }
    
    setExternalRangeCompare(null);
    setSelectedRangeCompare(range);
  };

  const handleRangeSelectCancel = useCallback(() => {
    setSelectedRange(null);
    setExternalRange(null);
    setSelectedFrame(null);
    setSelectedFrameRange(null);
    setCurrentFrameRange('');
    if (compareMode) {
      setSelectedRangeCompare(null);
      setExternalRangeCompare(null);
      setSelectedFrameCompare(null);
      setSelectedFrameRangeCompare(null);
      setCurrentFrameRangeCompare('');
    }
  }, [compareMode]);

  // Drawing completion handlers
  const handleDrawingComplete = useCallback(() => {
    const currentState = { shapes: [...shapes], selectedShapeId };
    const lastState = drawingHistory[currentHistoryIndex];
    if (JSON.stringify(currentState) !== JSON.stringify(lastState)) {
      const newHistory = drawingHistory.slice(0, currentHistoryIndex + 1);
      newHistory.push(currentState);
      setDrawingHistory(newHistory);
      setCurrentHistoryIndex(newHistory.length - 1);
    }
  }, [shapes, selectedShapeId, drawingHistory, currentHistoryIndex]);

  const handleDrawingCompleteCompare = useCallback(() => {
    const currentState = { shapes: [...shapesCompare], selectedShapeId: selectedShapeIdCompare };
    const lastState = drawingHistoryCompare[currentHistoryIndexCompare];
    if (JSON.stringify(currentState) !== JSON.stringify(lastState)) {
      const newHistory = drawingHistoryCompare.slice(0, currentHistoryIndexCompare + 1);
      newHistory.push(currentState);
      setDrawingHistoryCompare(newHistory);
      setCurrentHistoryIndexCompare(newHistory.length - 1);
    }
  }, [shapesCompare, selectedShapeIdCompare, drawingHistoryCompare, currentHistoryIndexCompare]);

  // Undo/Redo handlers
  const handleUndo = useCallback(() => {
    if (currentHistoryIndex > 0) {
      const newIndex = currentHistoryIndex - 1;
      const previousState = drawingHistory[newIndex];
      setCurrentHistoryIndex(newIndex);
      setShapes([...previousState.shapes]);
      setSelectedShapeId(previousState.selectedShapeId);
    }
  }, [currentHistoryIndex, drawingHistory]);

  const handleRedo = useCallback(() => {
    if (currentHistoryIndex < drawingHistory.length - 1) {
      const newIndex = currentHistoryIndex + 1;
      const nextState = drawingHistory[newIndex];
      setCurrentHistoryIndex(newIndex);
      setShapes([...nextState.shapes]);
      setSelectedShapeId(nextState.selectedShapeId);
    }
  }, [currentHistoryIndex, drawingHistory]);

  const handleUndoCompare = useCallback(() => {
    if (currentHistoryIndexCompare > 0) {
      const newIndex = currentHistoryIndexCompare - 1;
      const previousState = drawingHistoryCompare[newIndex];
      setCurrentHistoryIndexCompare(newIndex);
      setShapesCompare([...previousState.shapes]);
      setSelectedShapeIdCompare(previousState.selectedShapeId);
    }
  }, [currentHistoryIndexCompare, drawingHistoryCompare]);

  const handleRedoCompare = useCallback(() => {
    if (currentHistoryIndexCompare < drawingHistoryCompare.length - 1) {
      const newIndex = currentHistoryIndexCompare + 1;
      const nextState = drawingHistoryCompare[newIndex];
      setCurrentHistoryIndexCompare(newIndex);
      setShapesCompare([...nextState.shapes]);
      setSelectedShapeIdCompare(nextState.selectedShapeId);
    }
  }, [currentHistoryIndexCompare, drawingHistoryCompare]);

  // Drawing action handlers
  const handleDeleteSelected = () => {
    if (selectedShapeId) {
      const updatedShapes = shapes.filter(shape => shape.id !== selectedShapeId);
      setShapes(updatedShapes);
      setSelectedShapeId(null);
      
      const currentState = { shapes: updatedShapes, selectedShapeId: null };
      const newHistory = drawingHistory.slice(0, currentHistoryIndex + 1);
      newHistory.push(currentState);
      setDrawingHistory(newHistory);
      setCurrentHistoryIndex(newHistory.length - 1);
    }
  };

  const handleDeleteSelectedCompare = () => {
    if (selectedShapeIdCompare) {
      const updatedShapes = shapesCompare.filter(shape => shape.id !== selectedShapeIdCompare);
      setShapesCompare(updatedShapes);
      setSelectedShapeIdCompare(null);
      
      const currentState = { shapes: updatedShapes, selectedShapeId: null };
      const newHistory = drawingHistoryCompare.slice(0, currentHistoryIndexCompare + 1);
      newHistory.push(currentState);
      setDrawingHistoryCompare(newHistory);
      setCurrentHistoryIndexCompare(newHistory.length - 1);
    }
  };

  const handleChangeSelectedColor = (newColor: string) => {
    if (selectedShapeId) {
      const updatedShapes = shapes.map(shape => 
        shape.id === selectedShapeId 
          ? { ...shape, color: newColor }
          : shape
      );
      setShapes(updatedShapes);
      
      const currentState = { shapes: updatedShapes, selectedShapeId };
      const newHistory = drawingHistory.slice(0, currentHistoryIndex + 1);
      newHistory.push(currentState);
      setDrawingHistory(newHistory);
      setCurrentHistoryIndex(newHistory.length - 1);
    }
  };

  const handleChangeSelectedColorCompare = (newColor: string) => {
    if (selectedShapeIdCompare) {
      const updatedShapes = shapesCompare.map(shape => 
        shape.id === selectedShapeIdCompare 
          ? { ...shape, color: newColor }
          : shape
      );
      setShapesCompare(updatedShapes);
      
      const currentState = { shapes: updatedShapes, selectedShapeId: selectedShapeIdCompare };
      const newHistory = drawingHistoryCompare.slice(0, currentHistoryIndexCompare + 1);
      newHistory.push(currentState);
      setDrawingHistoryCompare(newHistory);
      setCurrentHistoryIndexCompare(newHistory.length - 1);
    }
  };

  const handleTextToolClick = useCallback(() => {}, []);
  const handleTextToolClickCompare = useCallback(() => {}, []);

  const exitDrawingToolbox = useCallback(() => {
    flushSync(() => {
      setDrawingTool('none');
      drawingToolRef.current = 'none';
      setSelectedShapeId(null);
      setSelectedShapeIdCompare(null);
      setCloseDrawingPopover(true);
    });
  }, []);

  // Comment handlers
  const handleCommentSelect = (comment: CommentType) => {
    // Seçili comment'i güncelle
    setSelectedCommentId(comment.id);
    
    // Yorumun ait olduğu versiyonu seçili versiyon olarak ayarla
    if (comment.versionId && comment.versionId !== selectedVersion) {
      setSelectedVersion(comment.versionId);
    }
    
    // Player 1'i aktif yap
    setActivePlayer(1);
    
    // Diğer player'ın range'ini temizle
    setSelectedFrameRangeCompare(null);
    setSelectedRangeCompare(null);
    setExternalRangeCompare(null);
    
    let shapesFromComment: Shape[] = [];
    try {
      if (comment.data) {
        const parsedData = JSON.parse(comment.data);
        shapesFromComment = parsedData.shapes || [];
      }
    } catch (error) {
      // Comment data parse error
    }

    setShapes(shapesFromComment);
    setSelectedShapeId(null);
    setDrawingHistory([
      {
        shapes: shapesFromComment.map((s) => ({ ...s })),
        selectedShapeId: null,
      },
    ]);
    setCurrentHistoryIndex(0);
    setIsPaused(true);
    setSelectedFrame(comment.startFrame);
    setSelectedFrameRange(comment.startFrame !== comment.endFrame ? [comment.startFrame, comment.endFrame] : null);

    setCurrentFrameIndex(comment.startFrame);

    if (primaryViewerMediaKind === 'pdf') {
      requestPdfPageJump(comment.startFrame);
      setCurrentVideoTime(Math.max(0, (comment.startFrame - 1) / FPS));
      setExternalRange(null);
      setSelectedRange(null);
    } else {
      const frameTime = comment.startFrame / FPS;
      setCurrentVideoTime(frameTime);
      const startTime = comment.startFrame / FPS;
      const endTime = comment.endFrame / FPS;
      setExternalRange([startTime, endTime]);
    }

    setCurrentFrameRange(
      comment.startFrame === comment.endFrame
        ? `${comment.startFrame}`
        : `${comment.startFrame}-${comment.endFrame}`,
    );
    commentSelectionSourcePlayerRef.current = 1;
  };

  const handleCommentSelectCompare = (comment: CommentType) => {
    // Seçili comment'i güncelle
    setSelectedCommentId(comment.id);
    
    // Yorumun ait olduğu versiyonu compare versiyonu olarak ayarla
    if (comment.versionId && comment.versionId !== selectedVersionCompare) {
      setSelectedVersionCompare(comment.versionId);
    }
    
    // Player 2'yi aktif yap
    setActivePlayer(2);
    
    // Diğer player'ın range'ini temizle
    setSelectedFrameRange(null);
    setSelectedRange(null);
    
    let shapesFromComment: Shape[] = [];
    try {
      if (comment.data) {
        const parsedData = JSON.parse(comment.data);
        shapesFromComment = parsedData.shapes || [];
      }
    } catch (error) {
      // Comment data parse error
    }

    setShapesCompare(shapesFromComment);
    setSelectedShapeIdCompare(null);
    setDrawingHistoryCompare([
      {
        shapes: shapesFromComment.map((s) => ({ ...s })),
        selectedShapeId: null,
      },
    ]);
    setCurrentHistoryIndexCompare(0);
    setIsPausedCompare(true);
    setSelectedFrameCompare(comment.startFrame);
    setSelectedFrameRangeCompare(comment.startFrame !== comment.endFrame ? [comment.startFrame, comment.endFrame] : null);
    
    const frameTime = comment.startFrame / FPS;
    setCurrentFrameIndexCompare(comment.startFrame);
    setCurrentVideoTimeCompare(frameTime);
    
    const startTime = comment.startFrame / FPS;
    const endTime = comment.endFrame / FPS;
    setSelectedRangeCompare(null);
    setExternalRangeCompare([startTime, endTime]);
    commentSelectionSourcePlayerRef.current = 2;
  };

  const handleReplyCompositionChange = useCallback((player: 1 | 2 | null) => {
    if (player != null) {
      setReplyCompositionPlayer(player);
      const emptyHist = { shapes: [] as Shape[], selectedShapeId: null as string | null };
      if (player === 1) {
        setShapes([]);
        setSelectedShapeId(null);
        setDrawingHistory([emptyHist]);
        setCurrentHistoryIndex(0);
      } else {
        setShapesCompare([]);
        setSelectedShapeIdCompare(null);
        setDrawingHistoryCompare([emptyHist]);
        setCurrentHistoryIndexCompare(0);
      }
      return;
    }
    setReplyCompositionPlayer(null);
    const cid = selectedCommentIdRef.current;
    if (cid == null) return;
    const src = commentSelectionSourcePlayerRef.current;
    let comment: CommentType | null = null;
    for (const v of versionsForComments) {
      const flat = flattenCommentsWithNestedReplies(v.comments);
      const c = flat.find((x: any) => x.id === cid);
      if (c) {
        comment = { ...c, versionId: v.id, versionName: v.name };
        break;
      }
    }
    if (!comment) return;
    let shapesFromComment: Shape[] = [];
    try {
      if (comment.data) {
        const parsedData = JSON.parse(comment.data);
        shapesFromComment = parsedData.shapes || [];
      }
    } catch {
      // ignore
    }
    const hist = {
      shapes: shapesFromComment.map((s) => ({ ...s })),
      selectedShapeId: null as string | null,
    };
    if (src === 1) {
      setShapes(shapesFromComment);
      setDrawingHistory([hist]);
      setCurrentHistoryIndex(0);
    } else if (src === 2) {
      setShapesCompare(shapesFromComment);
      setDrawingHistoryCompare([hist]);
      setCurrentHistoryIndexCompare(0);
    }
  }, [versionsForComments]);

  const handleClearCommentSelection = useCallback(() => {
    const src = commentSelectionSourcePlayerRef.current;
    commentSelectionSourcePlayerRef.current = null;
    setReplyCompositionPlayer(null);
    setSelectedCommentId(null);
    const emptyHist = { shapes: [] as Shape[], selectedShapeId: null as string | null };
    if (src === 1) {
      setShapes([]);
      setSelectedShapeId(null);
      setDrawingHistory([emptyHist]);
      setCurrentHistoryIndex(0);
    } else if (src === 2) {
      setShapesCompare([]);
      setSelectedShapeIdCompare(null);
      setDrawingHistoryCompare([emptyHist]);
      setCurrentHistoryIndexCompare(0);
    }
  }, []);

  const handleCommentPinClick = (commentId: number) => {
    // Seçili comment'i güncelle
    setSelectedCommentId(commentId);
    
    // This will be handled by CommentSection's getFormattedComments
    const groupedComments = getAllComments();
    const allComments: CommentType[] = [];
    
    Object.values(groupedComments).forEach((dateComments: any) => {
      allComments.push(...dateComments);
    });
    
    const comment = allComments.find((c: CommentType) => c.id === commentId);
    if (comment) {
      handleCommentSelect(comment);
    }
  };

  // Get all comments helper (simplified version for pin clicks)
  const getAllComments = () => {
    if (!versionsForComments.length) return {};
    
    let allComments: CommentType[] = [];
    
    if (showAllComments) {
      // Compare modunda ise sadece compare edilen versiyonların yorumlarını göster
      if (fileDetailCompareUiActive) {
        // Compare edilen iki versiyonun yorumlarını göster
        const compareVersions = [selectedVersion, selectedVersionCompare].filter(Boolean);
        versionsForComments.forEach((version: any) => {
          if (compareVersions.includes(version.id)) {
            const versionComments = flattenCommentsWithNestedReplies(version.comments).map(
              (comment: any) => ({
                ...comment,
                versionId: version.id,
                versionName: version.name,
              }),
            );
            allComments = [...allComments, ...versionComments];
          }
        });
      } else {
        // Normal modda tüm (görünür) versiyonların yorumlarını göster
        versionsForComments.forEach((version: any) => {
          const versionComments = flattenCommentsWithNestedReplies(version.comments).map(
            (comment: any) => ({
              ...comment,
              versionId: version.id,
              versionName: version.name,
            }),
          );
          allComments = [...allComments, ...versionComments];
        });
      }
    } else {
      // showAllComments false ise aktif player'daki versiyonun yorumlarını göster
      if (fileDetailCompareUiActive) {
        // Compare modunda aktif player'a göre versiyon seç
        let targetVersion: number;
        if (activePlayer === 1) {
          targetVersion = selectedVersion;
        } else {
          targetVersion = selectedVersionCompare || selectedVersion;
        }
        
        const selectedVersionData = versionsForComments.find((v: any) => v.id === targetVersion);
        if (selectedVersionData) {
          allComments = flattenCommentsWithNestedReplies(selectedVersionData.comments).map(
            (comment: any) => ({
              ...comment,
              versionId: selectedVersionData.id,
              versionName: selectedVersionData.name,
            }),
          );
        }
      } else {
        // Normal modda seçili versiyonun yorumlarını göster
        const selectedVersionData = versionsForComments.find((v: any) => v.id === selectedVersion);
        if (selectedVersionData) {
          allComments = flattenCommentsWithNestedReplies(selectedVersionData.comments).map(
            (comment: any) => ({
              ...comment,
              versionId: selectedVersionData.id,
              versionName: selectedVersionData.name,
            }),
          );
        }
      }
    }
    
    const groupedComments = allComments.reduce((groups: any, comment: CommentType) => {
      const date = formatApiUtcToLocaleDate(comment.createdAt);
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(comment);
      return groups;
    }, {});
    
    Object.keys(groupedComments).forEach(date => {
      groupedComments[date].sort((a: CommentType, b: CommentType) => 
        getApiUtcTimeMs(a.createdAt) - getApiUtcTimeMs(b.createdAt)
      );
    });
    
    return groupedComments;
  };

  // Get formatted comments for video player
  const getFormattedComments = () => {
    const groupedComments = getAllComments();
    const allComments: CommentType[] = [];
    
    Object.values(groupedComments).forEach((dateComments: any) => {
      allComments.push(...dateComments);
    });
    
    if (!allComments || allComments.length === 0) return [];
    
    return allComments.map((comment: CommentType) => ({
      id: comment.id,
      startFrame: comment.startFrame,
      endFrame: comment.endFrame,
      text: comment.text,
      shapes: comment.shapes || [],
      user: comment.user,
      isResolved: (comment as any).isResolved,
      versionId: (comment as any).versionId as number | undefined,
    }));
  };

  const handleFileDetailRefresh = async (options?: { preservePlayback?: boolean }) => {
    if (fileDetail?.id) {
      const res = await getFileById(fileDetail.id);
      if (res.success && res.data) {
        const previousVersionCount = fileDetail?.versions?.length || 0;
        const currentVersionCount = res.data.versions?.length || 0;
        
        if (currentVersionCount > previousVersionCount) {
          const latestVersion = res.data.versions.reduce((latest: any, current: any) =>
            getApiUtcTimeMs(current.createdAt) > getApiUtcTimeMs(latest.createdAt) ? current : latest
          );

          const targetPanel = addVersionTargetPanelRef.current;
          if (fileDetailCompareUiActive && targetPanel && latestVersion) {
            addVersionTargetPanelRef.current = null;
            skipVersionEffectRef.current = true;
            setFileDetail(res.data);
            if (targetPanel === 1) {
              setSelectedVersion(latestVersion.id);
            } else {
              setSelectedVersionCompare(latestVersion.id);
            }
            return;
          }
        }

        if (options?.preservePlayback) {
          skipVersionEffectRef.current = true;
          skipPlaybackResetRef.current = true;
          setFileDetail(res.data);
          return;
        }

        setFileDetail(res.data);
        setCurrentVideoTime(0);
        setCurrentFrameIndex(null);
        
        if (currentVersionCount > previousVersionCount) {
          const latestVersion = res.data.versions.reduce((latest: any, current: any) =>
            getApiUtcTimeMs(current.createdAt) > getApiUtcTimeMs(latest.createdAt) ? current : latest
          );
          if (latestVersion && latestVersion.path) {
            window.location.href = `/${latestVersion.path}`;
          }
        }
      }
    }
  };

  const triggerAddVersionUpload = () => {
    addVersionInputRef.current?.click();
  };

  const handleCompareAddVersion = (panel: 1 | 2) => {
    addVersionTargetPanelRef.current = panel;
    triggerAddVersionUpload();
  };

  const handleAddVersionFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!canCreate) {
      message.error('You do not have permission to add a file version.');
      return;
    }
    if (!file || !fileDetail?.projectId || !fileDetail?.id) return;
    const MAX_FILE_SIZE = 500 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      message.warning(t('projects.fileUpload.fileSizeExceeded'));
      return;
    }
    try {
      message.loading({ content: t('projects.fileUpload.uploadFile') || 'Uploading...', key: 'addVersion', duration: 0 });
      const result = await createFile({
        name: fileNameWithoutExtension(fileDetail.name),
        file,
        projectId: fileDetail.projectId,
        fileId: fileDetail.id,
        status: ApiFilePostStatusEnum.NeedsReview,
        share: [],
      });
      if (result?.success) {
        await handleFileDetailRefresh();
        message.destroy('addVersion');
        message.success(t('common.success') || 'Version added');
      } else {
        message.destroy('addVersion');
        message.error(t('projects.errors.uploadError'));
      }
    } catch {
      message.destroy('addVersion');
      message.error(t('projects.errors.uploadError'));
    }
  };

  // Go back handler
  const handleGoBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  // Approval modal handlers
  const handleApprovalConfirm = async (_changeSubentries: boolean) => {
    setApprovalLoading(true);
    try {
      await performStatusChange('Approved', true);
      setIsApprovalModalVisible(false);
      } catch (error) {
      // performStatusChange içinde hata mesajı gösteriliyor
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleApprovalCancel = () => {
    setIsApprovalModalVisible(false);
  };

  // Keyboard shortcuts
  // Status change functions
  const canChangeStatus = () => {
    if (!user) return false;
    return canChangeEntryStatus(user.role);
  };

  const getCurrentStatus = () => {
    if (selectedVersion && fileDetail?.versions) {
      const selectedVersionData = fileDetail.versions.find((v: any) => v.id === selectedVersion);
      if (selectedVersionData) {
        return selectedVersionData.status || fileDetail.status || 'NeedsReview';
      }
    }
    return fileDetail?.status || 'NeedsReview';
  };

  const countUnresolvedComments = () => {
    let unresolvedComments = 0;
    versionsForComments.forEach((version: any) => {
      flattenCommentsWithNestedReplies(version.comments).forEach((comment: any) => {
        if (!comment.isResolved) {
          unresolvedComments++;
        }
      });
    });
    return unresolvedComments;
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!canChangeStatus()) {
      message.error('You do not have permission for this action.');
      return;
    }

    // Approved seçildiğinde resolve kontrolü yap
    if (newStatus === 'Approved') {
      const unresolvedComments = countUnresolvedComments();
      handleApprovalProcess(newStatus, unresolvedComments);
      return;
    }

    // Diğer statuslar için direkt servise git
    await performStatusChange(newStatus, false);
  };

  const handleApprovalProcess = (newStatus: string, unresolvedComments: number) => {
    if (newStatus === 'Approved') {
      setIsApprovalModalVisible(true);
    } else {
      performStatusChange('Updated', false);
    }
  };

  const performStatusChange = async (newStatus: string, changeSubentries: boolean) => {
    // setStatusLoading(true);
    try {
      const entries = [];
      
      if (fileDetail) {
        const versionId = selectedVersion || fileDetail.id;
        entries.push({
          id: versionId,
          entryType: selectedVersion ? 'Version' : (fileDetail.entryType || 'File'),
          entryStatus: newStatus,
          changeSubentries: changeSubentries
        });
      }

      if (entries.length > 0) {
        const response = await changeStatus(entries);
        if (response.success) {
          message.success('Status changed successfully.');
          // setIsStatusModalVisible(false);
          handleFileDetailRefresh();
        } else {
          if (response.message === 'Unauthorized') {
            message.error('You do not have permission to change status for these items.');
          } else {
            throw new Error(response.message || 'Status change failed');
          }
        }
      }
    } catch (error) {
      message.error('Network error or insufficient permissions. Please try again.');
    } finally {
      // setStatusLoading(false);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inEditable =
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.contentEditable === 'true');

      if (e.key === 'Escape' && !inEditable && drawingTool !== 'none') {
        e.preventDefault();
        exitDrawingToolbox();
        return;
      }

      if (isDrawingMode) {
        if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
          e.preventDefault();
          handleUndo();
        } else if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
          e.preventDefault();
          handleRedo();
        } else if ((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) {
          e.preventDefault();
          handleRedo();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isDrawingMode, drawingTool, exitDrawingToolbox, handleUndo, handleRedo]);

  const [isMobileViewport, setIsMobileViewport] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 800
  );
  const [isCoarsePointerDevice, setIsCoarsePointerDevice] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(hover: none) and (pointer: coarse)').matches
  );
  const [hasFinePointer, setHasFinePointer] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches
  );
  const [isLandscapeOrientation, setIsLandscapeOrientation] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(orientation: landscape)').matches
  );
  const [compareLandscapeCommentDrawerOpen, setCompareLandscapeCommentDrawerOpen] =
    useState(false);
  const [headerSlotEl, setHeaderSlotEl] = useState<HTMLElement | null>(null);
  const commentContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onResize = () => setIsMobileViewport(window.innerWidth <= 800);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(orientation: landscape)');
    const sync = () => setIsLandscapeOrientation(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    window.addEventListener('resize', sync);
    return () => {
      mq.removeEventListener('change', sync);
      window.removeEventListener('resize', sync);
    };
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(hover: none) and (pointer: coarse)');
    const sync = () => setIsCoarsePointerDevice(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => {
      mq.removeEventListener('change', sync);
    };
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(hover: hover) and (pointer: fine)');
    const sync = () => setHasFinePointer(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => {
      mq.removeEventListener('change', sync);
    };
  }, []);

  /** Yorum çubuğu ikonu + yan çekmece: yalnızca mobil cihaz + landscape + video compare */
  const landscapeCompareCommentUi =
    (isMobileViewport || isCoarsePointerDevice) &&
    fileDetailCompareUiActive &&
    isLandscapeOrientation;

  useEffect(() => {
    if (!landscapeCompareCommentUi) {
      setCompareLandscapeCommentDrawerOpen(false);
    }
  }, [landscapeCompareCommentUi]);

  useEffect(() => {
    if (!compareLandscapeCommentDrawerOpen || !landscapeCompareCommentUi) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setCompareLandscapeCommentDrawerOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [compareLandscapeCommentDrawerOpen, landscapeCompareCommentUi]);

  /** Desktop compare: ESC ile çık (range/çizim öncelikli; mobilde yok) */
  useEffect(() => {
    if (!fileDetailCompareUiActive || isMobileViewport) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;

      const target = e.target as HTMLElement | null;
      const inEditable =
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.contentEditable === 'true');
      if (inEditable) return;

      if (drawingTool !== 'none') return;

      if (compareLandscapeCommentDrawerOpen && landscapeCompareCommentUi) {
        e.preventDefault();
        setCompareLandscapeCommentDrawerOpen(false);
        return;
      }

      const rangeActive =
        selectedRange != null ||
        selectedRangeCompare != null ||
        externalRange != null ||
        externalRangeCompare != null;
      if (rangeActive) return;

      e.preventDefault();
      exitCompareFromToolbar();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    fileDetailCompareUiActive,
    isMobileViewport,
    drawingTool,
    compareLandscapeCommentDrawerOpen,
    landscapeCompareCommentUi,
    selectedRange,
    selectedRangeCompare,
    externalRange,
    externalRangeCompare,
    exitCompareFromToolbar,
  ]);

  const compareCommentToggleSlot = useMemo(
    () =>
      landscapeCompareCommentUi ? (
        <Button
          type="text"
          size="small"
          icon={<CommentOutlined className={styles.compareLandscapeCommentToggleIcon} />}
          onClick={() =>
            setCompareLandscapeCommentDrawerOpen((open) => !open)
          }
          aria-expanded={compareLandscapeCommentDrawerOpen}
          aria-label={t('common.comments')}
          title={t('common.comments')}
          className={`${styles.compareLandscapeCommentToggleBtn}${
            compareLandscapeCommentDrawerOpen
              ? ` ${styles.compareLandscapeCommentToggleBtnActive}`
              : ''
          }`}
        />
      ) : undefined,
    [landscapeCompareCommentUi, compareLandscapeCommentDrawerOpen, t]
  );

  /** Mobil yorum input: yalnızca yazılım klavyesi açıldığında paneli visualViewport'a sığdır */
  useEffect(() => {
    if (!isMobileViewport || hasFinePointer || !window.visualViewport) return;

    const vv = window.visualViewport;
    const container = commentContainerRef.current;
    if (!container) return;

    const KEYBOARD_HEIGHT_THRESHOLD_PX = 100;
    const LAYOUT_VIEWPORT_GAP_THRESHOLD_PX = 80;
    const FOCUS_KEYBOARD_SETTLE_MS = 300;

    let focusBaselineHeight: number | null = null;
    let focusCheckTimer: ReturnType<typeof setTimeout> | null = null;

    const clearContainerKeyboardStyles = () => {
      container.style.maxHeight = '';
      container.style.height = '';
    };

    const isLikelySoftwareKeyboard = (baselineHeight: number) => {
      const heightDrop = baselineHeight - vv.height;
      const layoutGap = window.innerHeight - vv.height;
      return (
        heightDrop > KEYBOARD_HEIGHT_THRESHOLD_PX &&
        layoutGap > LAYOUT_VIEWPORT_GAP_THRESHOLD_PX
      );
    };

    const applyKeyboardLayoutIfNeeded = (baselineHeight: number) => {
      const isInputFocused =
        container.querySelector('textarea:focus, input:focus') !== null;
      if (!isInputFocused) {
        clearContainerKeyboardStyles();
        return;
      }

      if (!isLikelySoftwareKeyboard(baselineHeight)) {
        clearContainerKeyboardStyles();
        return;
      }

      const available = vv.height - vv.offsetTop;
      container.style.maxHeight = `${available}px`;
      container.style.height = `${available}px`;

      requestAnimationFrame(() => {
        const input = container.querySelector(
          'textarea:focus, input:focus'
        ) as HTMLElement | null;
        input?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    };

    const resetFocusBaseline = () => {
      focusBaselineHeight = null;
      if (focusCheckTimer !== null) {
        clearTimeout(focusCheckTimer);
        focusCheckTimer = null;
      }
    };

    const handleViewportResize = () => {
      const baseline = focusBaselineHeight ?? vv.height;
      applyKeyboardLayoutIfNeeded(baseline);
    };

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName !== 'TEXTAREA' && target.tagName !== 'INPUT') return;

      resetFocusBaseline();
      focusBaselineHeight = vv.height;
      focusCheckTimer = setTimeout(() => {
        focusCheckTimer = null;
        applyKeyboardLayoutIfNeeded(focusBaselineHeight ?? vv.height);
      }, FOCUS_KEYBOARD_SETTLE_MS);
    };

    const handleFocusOut = () => {
      resetFocusBaseline();
      setTimeout(() => {
        if (!container.querySelector('textarea:focus, input:focus')) {
          clearContainerKeyboardStyles();
        }
      }, 100);
    };

    const handleOrientationChange = () => {
      resetFocusBaseline();
      clearContainerKeyboardStyles();
    };

    vv.addEventListener('resize', handleViewportResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    container.addEventListener('focusin', handleFocusIn);
    container.addEventListener('focusout', handleFocusOut);

    return () => {
      resetFocusBaseline();
      vv.removeEventListener('resize', handleViewportResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
      container.removeEventListener('focusin', handleFocusIn);
      container.removeEventListener('focusout', handleFocusOut);
      clearContainerKeyboardStyles();
    };
  }, [isMobileViewport, hasFinePointer]);

  useLayoutEffect(() => {
    if (loading || !fileDetail || isVersionsPageRoute) {
      setHeaderSlotEl(null);
      return;
    }
    const syncSlot = () => {
      setHeaderSlotEl(document.getElementById(FILE_DETAIL_MOBILE_HEADER_SLOT_ID));
    };
    syncSlot();
    const raf = requestAnimationFrame(syncSlot);
    /* Mobil: resize veya auth sonrası slot bir tick sonra geliyor; portal kaçırılırsa üst aksiyonlar boş kalıyordu */
    const retryId =
      isMobileViewport ? window.setTimeout(syncSlot, 0) : undefined;
    return () => {
      cancelAnimationFrame(raf);
      if (retryId !== undefined) window.clearTimeout(retryId);
    };
  }, [loading, fileDetail, isVersionsPageRoute, isMobileViewport]);

  // Computed values
  const canUndo = currentHistoryIndex > 0;
  const canRedo = currentHistoryIndex < drawingHistory.length - 1;
  const canUndoCompare = currentHistoryIndexCompare > 0;
  const canRedoCompare = currentHistoryIndexCompare < drawingHistoryCompare.length - 1;

  const fileMessageState = (p: { title: string; hint?: string; actionLabel: string; onAction: () => void }) => (
    <MainLayout>
      <Row className={styles.projectDetailEmptyWrap}>
        <Col span={24}>
          <ProjectFolderEmptyState
            variant={FILE_DETAIL_VARIANT}
            messageOnly
            title={p.title}
            hint={p.hint ?? ''}
            actionLabel={p.actionLabel}
            onAction={p.onAction}
          />
        </Col>
      </Row>
    </MainLayout>
  );

  // Loading state
  if (loading) {
    return null;
  }

  if (loadAccessDenied) {
    return fileMessageState({
      title: t('fileDetail.accessDenied'),
      hint: t('fileDetail.accessDeniedHint'),
      actionLabel: t('fileDetail.goBack'),
      onAction: handleGoBack,
    });
  }

  // File not found state
  if (!fileDetail) {
    return fileMessageState({
      title: t('fileDetail.fileNotFound'),
      hint: t('fileDetail.fileNotFoundHint'),
      actionLabel: t('fileDetail.goBack'),
      onAction: handleGoBack,
    });
  }

  if (
    isGuest &&
    (fileDetail.versions?.length ?? 0) > 0 &&
    versions.length === 0 &&
    !versionsLoading
  ) {
    return fileMessageState({
      title: t('fileDetail.noSharedVersionTitle'),
      hint: t('fileDetail.noSharedVersionHint'),
      actionLabel: t('fileDetail.goBack'),
      onAction: handleGoBack,
    });
  }

  // Versions page
  if (isVersionsPageRoute) {
    if (loading) {
      return null;
    }
    if (!fileDetail) {
      return fileMessageState({
        title: t('fileDetail.fileNotFound'),
        hint: t('fileDetail.fileNotFoundHint'),
        actionLabel: t('fileDetail.goBack'),
        onAction: handleGoBack,
      });
    }
    return (
      <FileVersions 
        fileDetail={fileDetail} 
        projectName={projectName || ''}
        
      />
    );
  }

  const usePortalForMobileHeader = isMobileViewport && headerSlotEl !== null;

  const playerTopBarVersionManager = (
    <VersionManager
      fileDetail={fileDetail}
      versions={versions}
      versionsLoading={versionsLoading}
      selectedVersion={selectedVersion}
      compareMode={false}
      selectPopupClassName={styles.playerPageTopBarSelectDropdown}
      popupMatchSelectWidth={false}
      onVersionChange={(versionName) => {
        const versionData = versions.find((v: any) => v.name === versionName);
        if (versionData) setSelectedVersion(versionData.id);
        if (compareMode) {
          closeCompareMode();
        }
      }}
      onAddVersionClick={canCreate ? triggerAddVersionUpload : undefined}
      addVersionButtonLabel="+ Add Version"
    />
  );

  const fileDetailMetaToolbar = (
    <div className={styles.playerPageTopBarMeta}>
      {canChangeStatus() && (
        <StatusSelector
          className={styles.statusSelector}
          selectPopupExtraClassName={styles.playerPageTopBarSelectDropdown}
          popupMatchSelectWidth={false}
          onChange={handleStatusChange}
          disabled={false}
          userRole={user?.role}
          selectedItems={new Set([selectedVersion || fileDetail?.id])}
          selectedItemsDetails={[{
            id: selectedVersion || fileDetail?.id,
            name: fileDetail?.name || 'File',
            entryType: selectedVersion ? 'Version' : 'File',
            status: getCurrentStatus()
          }]}
          fileDetail={fileDetail}
          currentEntry={null}
          selectedVersion={selectedVersion}
          canMultiSelect={() => false}
        />
      )}
      {playerTopBarVersionManager}
    </div>
  );

  const fileDetailFooterToolbar = (
    <FooterActions
      isHeaderless
      barLayout="playerTopBar"
      variant={FILE_DETAIL_VARIANT}
      fileViewerMediaKind={primaryViewerMediaKind}
      compareModeActive={fileDetailCompareUiActive}
      onCompareExit={exitCompareFromToolbar}
      onCompare={() => {
        if (!compareMode) {
          setCompareMode(true);
        }
        resetAllStates();
      }}
      onDelete={handleDelete}
      onDownload={handleDownload}
      onCopyUrl={handleCopyUrl}
      fileDetail={fileDetail}
      selectedVersion={fileDetailCompareUiActive ? (selectedVersionCompare ?? undefined) : selectedVersion}
      selectedItems={selectedItems}
      selectedItemsDetails={selectedItemsDetails}
      currentEntry={fileDetailCompareUiActive && selectedVersionCompare
        ? versions.find((v: any) => v.id === selectedVersionCompare)
        : versions.find((v: any) => v.id === selectedVersion)}
    />
  );

  return (
    <div className={styles.fileDetailWrapper} style={FILE_DETAIL_THEME_VARS}>
      {usePortalForMobileHeader && headerSlotEl
        ? createPortal(
            <div className={`${styles.fileDetailToolsInAppHeader} ${styles.playerPageTopBarTools}`}>
              <div className={styles.playerPageTopBarRightInner}>
                {fileDetailFooterToolbar}
              </div>
            </div>,
            headerSlotEl
          )
        : null}
      {!isMobileViewport ? (
        <div className={styles.playerPageTopBar}>
          <div className={styles.playerPageTopBarRow}>
            <div className={styles.playerPageTopBarBreadcrumb}>
              <Breadcrumb className={breadcrumbStyles.topBar} apiItems={breadcrumbApiItems} />
            </div>
            <div className={styles.playerPageTopBarTools}>
              <div className={styles.playerPageTopBarRightInner}>
                {fileDetailMetaToolbar}
                {fileDetailFooterToolbar}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div
        className={`${styles.fileDetailBody}${
          landscapeCompareCommentUi && compareLandscapeCommentDrawerOpen
            ? ` ${styles.fileDetailBodyLandscapeCompareDrawerOpen}`
            : ''
        }`}
      >
        <div className={styles.fileDetailContainer}>
        {isMobileViewport ? (
          <>
            <div className={styles.fileDetailMobileBreadcrumb}>
              <Breadcrumb apiItems={breadcrumbApiItems} />
            </div>
            <div className={styles.fileDetailMobileMetaRow}>
              <div
                className={`${styles.playerPageTopBarRightInner} ${styles.fileDetailMobileMetaRowInner}`}
              >
                {navigableSiblingFiles.length > 1 ? (
                  <div className={styles.fileDetailMobileSiblingNav}>
                    <button
                      type="button"
                      className={styles.fileDetailMobileSiblingNavButton}
                      onClick={() => navigateSiblingByOffset('prev')}
                      disabled={siblingNavBusy || !canGoPrevSibling}
                      aria-label={t('fileDetail.siblingFileNavPrev')}
                    >
                      <LeftOutlined />
                    </button>
                    <span className={styles.fileDetailMobileSiblingNavCounter}>
                      {currentSiblingIndex >= 0 ? currentSiblingIndex + 1 : '-'}/
                      {navigableSiblingFiles.length}
                    </span>
                    <button
                      type="button"
                      className={styles.fileDetailMobileSiblingNavButton}
                      onClick={() => navigateSiblingByOffset('next')}
                      disabled={siblingNavBusy || !canGoNextSibling}
                      aria-label={t('fileDetail.siblingFileNavNext')}
                    >
                      <RightOutlined />
                    </button>
                  </div>
                ) : null}
                {fileDetailMetaToolbar}
                {!usePortalForMobileHeader ? (
                  <div className={styles.fileDetailMobileFooterInline}>
                    {fileDetailFooterToolbar}
                  </div>
                ) : null}
              </div>
            </div>
          </>
        ) : null}
        {/* Video Player Section */}
        <VideoPlayerSection
          fileDetail={fileDetail}
          versions={versions}
          versionsLoading={versionsLoading}
          fps={FPS}
          selectedCommentId={selectedCommentId}
          selectedVersion={selectedVersion}
          currentVideoUrl={currentVideoUrl}
          videoLoading={videoLoading}
          shapes={shapes}
          drawingTool={drawingTool}
          drawingColor={drawingColor}
          selectedShapeId={selectedShapeId}
          currentVideoTime={currentVideoTime}
          isPaused={isPaused}
          selectedRange={selectedRange}
          externalRange={externalRange}
          currentFrameIndex={currentFrameIndex ?? undefined}
          compareMode={fileDetailCompareUiActive}
          activePlayer={activePlayer}
          selectedVersionCompare={selectedVersionCompare}
          currentVideoUrlCompare={currentVideoUrlCompare}
          shapesCompare={shapesCompare}
          selectedShapeIdCompare={selectedShapeIdCompare}
          currentVideoTimeCompare={currentVideoTimeCompare}
          isPausedCompare={isPausedCompare}
          selectedRangeCompare={selectedRangeCompare}
          externalRangeCompare={externalRangeCompare}
          currentFrameIndexCompare={currentFrameIndexCompare ?? undefined}
          isDrawingMode={isDrawingMode && !hideDrawingToolbarForViewer}
          drawingToolbox={
            showDrawingToolbox ? (
              <div
                className={`${styles.drawingToolboxWrap}${
                  selectedCommentId != null && replyCompositionPlayer === null
                    ? ` ${styles.drawingToolboxWrapDisabled}`
                    : ''
                }`}
              >
                <div className={styles.drawingToolboxInner}>
                  <CompactDrawingToolbox
                    variant="controlBar"
                    tool={drawingTool}
                    color={drawingColor}
                    onToolChange={setDrawingTool}
                    onColorChange={setDrawingColor}
                    onClear={() =>
                      activePlayer === 1 ? setShapes([]) : setShapesCompare([])
                    }
                    onSave={() => {}}
                    onDeleteSelected={
                      activePlayer === 1
                        ? handleDeleteSelected
                        : handleDeleteSelectedCompare
                    }
                    onChangeSelectedColor={
                      activePlayer === 1
                        ? handleChangeSelectedColor
                        : handleChangeSelectedColorCompare
                    }
                    onUndo={activePlayer === 1 ? handleUndo : handleUndoCompare}
                    onRedo={activePlayer === 1 ? handleRedo : handleRedoCompare}
                    hasSelectedShape={
                      activePlayer === 1
                        ? !!selectedShapeId
                        : !!selectedShapeIdCompare
                    }
                    canUndo={activePlayer === 1 ? canUndo : canUndoCompare}
                    canRedo={activePlayer === 1 ? canRedo : canRedoCompare}
                    selectedShapeColor={
                      activePlayer === 1
                        ? selectedShapeId
                          ? shapes.find((s) => s.id === selectedShapeId)?.color
                          : undefined
                        : selectedShapeIdCompare
                          ? shapesCompare.find(
                              (s) => s.id === selectedShapeIdCompare
                            )?.color
                          : undefined
                    }
                    hasShapes={
                      activePlayer === 1
                        ? shapes.length > 0
                        : shapesCompare.length > 0
                    }
                  />
                </div>
                {selectedCommentId != null && replyCompositionPlayer === null ? (
                  <div
                    className={styles.drawingToolboxDisabledLayer}
                    role="presentation"
                    title={t('common.drawingDisabledWhileViewingComment')}
                  />
                ) : null}
              </div>
            ) : undefined
          }
          onVersionChange={(versionName) => {
            // Versiyon name'inden ID'yi bul
            const versionData = versions.find((v: any) => v.name === versionName);
            if (versionData) {
              setSelectedVersion(versionData.id);
            }
          }}
          onShapesChange={setShapes}
          onTextToolClick={handleTextToolClick}
          onShapeSelection={setSelectedShapeId}
          onDrawingComplete={handleDrawingComplete}
          onCurrentTimeChange={setCurrentVideoTime}
          onPlay={() => {
            if (drawingToolRef.current !== 'none') return;
            setShapes([]);
            setCurrentFrameIndex(null);
            setIsPaused(false);
            setReplyCompositionPlayer(null);
            setSelectedCommentId(null); // Comment seçimini sıfırla
          }}
          onSeek={() => {
            if (drawingToolRef.current !== 'none') return;
            setShapes([]);
            setCurrentFrameIndex(null);
            setIsPaused(false);
            setReplyCompositionPlayer(null);
            setSelectedCommentId(null); // Comment seçimini sıfırla
          }}
          onDurationChange={setVideoDuration}
          onRangeSelect={handleRangeSelect}
          onRangeSelectCancel={handleRangeSelectCancel}
          onExitDrawingToolbox={exitDrawingToolbox}
          onCompareVersionChange={(versionName) => {
            // Versiyon name'inden ID'yi bul
            const versionData = versions.find((v: any) => v.name === versionName);
            if (versionData) {
              setSelectedVersionCompare(versionData.id);
            }
          }}
          onShapesCompareChange={setShapesCompare}
          onTextToolClickCompare={handleTextToolClickCompare}
          onShapeSelectionCompare={setSelectedShapeIdCompare}
          onDrawingCompleteCompare={handleDrawingCompleteCompare}
          onCurrentTimeChangeCompare={setCurrentVideoTimeCompare}
          onPlayCompare={() => {
            if (drawingToolRef.current !== 'none') return;
            setShapesCompare([]);
            setCurrentFrameIndexCompare(null);
            setIsPausedCompare(false);
            setReplyCompositionPlayer(null);
            setSelectedCommentId(null); // Comment seçimini sıfırla
          }}
          onSeekCompare={() => {
            if (drawingToolRef.current !== 'none') return;
            setShapesCompare([]);
            setCurrentFrameIndexCompare(null);
            setIsPausedCompare(false);
            setReplyCompositionPlayer(null);
            setSelectedCommentId(null); // Comment seçimini sıfırla
          }}
          onDurationChangeCompare={(duration) => {}}
          onRangeSelectCompare={handleRangeSelectCompare}
          onActivePlayerChange={setActivePlayer}
          onCommentPinClick={handleCommentPinClick}
          getFormattedComments={getFormattedComments}
          suppressInlineVersionChrome
          onAddVersion={canCreate ? handleCompareAddVersion : undefined}
          onPdfPageChange={handlePdfPageChange}
          onPdfUserNavigate={handlePdfUserNavigate}
          pdfPageJumpRequest={pdfPageJumpRequest}
          onPdfDocumentInfo={({ numPages: n }) => setPdfNumPages(n)}
          compareDualSinglePanel={
            landscapeCompareCommentUi && compareLandscapeCommentDrawerOpen
          }
          compareCommentToggleSlot={compareCommentToggleSlot}
          onComparePanelClose={handleComparePanelClose}
          siblingFiles={siblingFiles}
          siblingFilesLoading={siblingFilesLoading}
          fileSwitchLoading={fileSwitchLoading}
          onNavigateToSibling={handleNavigateToSibling}
          hideSiblingOverlayNav={isMobileViewport}
        />
        </div>

      {/* Comment Section */}
      <div
        className={`${styles.commentContainer}${
          landscapeCompareCommentUi ? ` ${styles.commentContainerCompareLandscape}` : ''
        }${
          landscapeCompareCommentUi && compareLandscapeCommentDrawerOpen
            ? ` ${styles.commentContainerCompareLandscapeOpen}`
            : ''
        }`}
        ref={commentContainerRef}
      >
        {landscapeCompareCommentUi && compareLandscapeCommentDrawerOpen ? (
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            aria-label={t('common.close')}
            onClick={() => setCompareLandscapeCommentDrawerOpen(false)}
            className={styles.commentDrawerCloseFab}
          />
        ) : null}
        <div className={styles.commentContainerMain}>
        <CommentSection
          fileDetail={fileDetail}
          versions={versions}
          versionsLoading={versionsLoading}
          selectedVersion={selectedVersion}
          selectedVersionCompare={selectedVersionCompare}
          selectedCommentId={selectedCommentId}
          showAllComments={showAllComments}
          onShowAllCommentsChange={setShowAllComments}
          onVersionChange={(versionName) => {
            // Versiyon name'inden ID'yi bul
            const versionData = versions.find((v: any) => v.name === versionName);
            if (versionData) {
              setSelectedVersion(versionData.id);
            }
          }}
          canComment={canComment}
          canShowAllComments={canShowAllComments}
          canResolve={canResolve}
          userRole={user?.role}
          canAddFileVersion={!!canCreate}
          onAddNewFile={triggerAddVersionUpload}
          compareMode={fileDetailCompareUiActive}
          activePlayer={activePlayer}
          currentFrameRange={currentFrameRange}
          currentFrameRangeCompare={currentFrameRangeCompare}
          shapes={shapes}
          shapesCompare={shapesCompare}
          drawingTool={drawingTool}
          drawingColor={drawingColor}
          selectedShapeId={selectedShapeId}
          selectedShapeIdCompare={selectedShapeIdCompare}
          onShapesChange={setShapes}
          onShapesCompareChange={setShapesCompare}
                    onToolChange={setDrawingTool}
                    onColorChange={setDrawingColor}
          onClearShapes={() => activePlayer === 1 ? setShapes([]) : setShapesCompare([])}
          onSave={() => {}}
          onDeleteSelected={handleDeleteSelected}
          onDeleteSelectedCompare={handleDeleteSelectedCompare}
          onChangeSelectedColor={handleChangeSelectedColor}
          onChangeSelectedColorCompare={handleChangeSelectedColorCompare}
          onUndo={handleUndo}
          onRedo={handleRedo}
          onUndoCompare={handleUndoCompare}
          onRedoCompare={handleRedoCompare}
          canUndo={canUndo}
          canRedo={canRedo}
          canUndoCompare={canUndoCompare}
          canRedoCompare={canRedoCompare}
          currentVideoTime={currentVideoTime}
          currentFps={FPS}
          videoDuration={videoDuration}
          viewerMediaKind={primaryViewerMediaKind}
          pdfCurrentPage={pdfCurrentPage}
          pdfPageRef={pdfPageRef}
          pdfNumPages={pdfNumPages}
          pdfShowAllPageComments={pdfShowAllPageComments}
          onPdfShowAllPageCommentsChange={setPdfShowAllPageComments}
          selectedFrame={selectedFrame}
          selectedFrameRange={selectedFrameRange}
          selectedFrameCompare={selectedFrameCompare}
          selectedFrameRangeCompare={selectedFrameRangeCompare}
          onCommentSelect={handleCommentSelect}
          onCommentSelectCompare={handleCommentSelectCompare}
          onCommentSelectionClear={handleClearCommentSelection}
          onGuestCommentSubmit={handleFileDetailRefresh}
          onFileDetailRefresh={handleFileDetailRefresh}
          closeDrawingPopover={closeDrawingPopover}
          onCloseDrawingPopover={setCloseDrawingPopover}
          onDrawingToolbarReset={resetDrawingToolbarAfterCommentSend}
          onResetFrameSelection={() => {
            setSelectedFrame(null);
            setSelectedFrameRange(null);
            setSelectedFrameCompare(null);
            setSelectedFrameRangeCompare(null);
            setCurrentFrameRange('');
            setCurrentFrameRangeCompare('');
          }}
          hideVersionControls
          cardMenuVariant={FILE_DETAIL_VARIANT}
          onReplyCompositionChange={handleReplyCompositionChange}
                  />
        </div>
      </div>
      </div>

      <input
        ref={addVersionInputRef}
        id="file-detail-add-version-input"
        name="addVersion"
        type="file"
        accept="*/*"
        style={{ display: 'none' }}
        onChange={handleAddVersionFileChange}
      />
      {/* Approval Modal */}
      <ApprovalModal
        visible={isApprovalModalVisible}
        onCancel={handleApprovalCancel}
        onConfirm={handleApprovalConfirm}
        loading={approvalLoading}
        variant={FILE_DETAIL_VARIANT}
      />
    </div>
  );
};

export default FileDetail; 